import React, { Fragment, useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Typography,
  InputAdornment,
} from "@mui/material";
import AuthContext from "../Context/AuthProvider";
import { getFacilityUsers } from "../API/UsersApi";
import { getClinicalServices } from "../API/FacilitySettingsApi";
import { VisitTypes, PaymentModes } from "../Utility/Constants";
import SchedulingAssistant from "./SchedulingAssistant";
import SearchSelectPatient1 from "../Patients/SearchSelectPatient1";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { convertToUTC } from "../Utility/DateTimeUtility";
import { createNewVisit } from "../API/VisitsApi";
import { toBeChecked } from "@testing-library/jest-dom/matchers";
import { showLoadingImage } from "../Utility/Utility";
import { useSnackbar } from "../Utility/UserMessageContext";
import MenuBar from "../common/MenuBar";
import "../common/styles/CommonStyles.css"; // Import your custom styles
import GenericTextField from "../common/GenericTextField";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import GenericDatePicker from "../common/GenericDatePicker";
import GenericDropDown from "../common/GenericDropDown";

const NewVisit = ({ newAppointmentTempId, visitType, closeTab }) => {
  const { showSnackbar } = useSnackbar();
  const [selectedPatient, setSelectedPatient] = useState();
  const [availablePhysicians, setAvailablePhysicians] = useState();
  const { auth } = useContext(AuthContext);
  const [patientNotes, setPatientNotes] = useState();
  const [availableServices, setAvailableServices] = useState();
  const [selectedServiceId, setSelectedServiceId] = useState();
  const [selectedPhysicianId, setSelectedPhysicianId] = useState();
  const [selectedService, setSelectedService] = useState("");
  const [selectedPhysician, setSelectedPhysician] = useState();
  const [selectedDate, setSelectedDate] = useState();
  const [scheduledFrom, setScheduledFrom] = useState();
  const [scheduledTo, setScheduledTo] = useState();
  const [scheduledStartDateTime, setScheduledStartDateTime] = useState();
  const [scheduledEndDateTime, setScheduledEndDateTime] = useState();
  const [onwardTravelTimeInMins, setOnwardTravelTimeInMins] = useState(0);
  const [returnTravelTimeInMins, setReturnTravelTimeInMins] = useState(0);
  const [addingAppointment, setAddingAppointment] = useState(false);
  const [dataRestored, setDataRestored] = useState(false);
  const [saving, setSaving] = useState(false);
  const [referredBy, setReferredBy] = useState("");
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [shouldGenerateBill, setShouldGenerateBill] = useState(false);
  const [discountInPercentage, setDiscountInPercentage] = useState(0);
  const [payableAmount, setpayableAmount] = useState(0);
  useEffect(() => {
    const getPhysicians = async () => {
      var result = await getFacilityUsers(auth, {
        facilityId: auth.selectedFacility.id,
        roles: ["Physician"],
      });
      if (result?.isSuccess) {
        setAvailablePhysicians(result?.data);
      }
    };

    const setClinicalServices = async () => {
      const filters = {
        facilityId: auth.selectedFacility.id,
        visitType: visitType,
      };
      var result = await getClinicalServices(auth, filters);
      if (result.isSuccess) {
        console.log(result?.data);
        setAvailableServices(result?.data);
      } else {
        showSnackbar(
          "Error occurred while fetching clinical services.",
          "error"
        );
      }
    };

    const restoreData = () => {
      const savedNewVisit = localStorage.getItem(newAppointmentTempId);

      if (savedNewVisit) {
        const savedNewVisitJSON = JSON.parse(savedNewVisit);
        if (savedNewVisitJSON) {
          setSelectedPatient(savedNewVisitJSON.selectedPatient);
          setSelectedPhysicianId(savedNewVisitJSON.selectedPhysicianId);
          setSelectedServiceId(savedNewVisitJSON.selectedServiceId);
          setPatientNotes(savedNewVisitJSON.patientNotes);
          setReferredBy(savedNewVisitJSON.referredBy || "");
          setAdvanceAmount(savedNewVisitJSON.advanceAmount || "");
          setPaymentMode(savedNewVisitJSON.paymentMode || "");
          setShouldGenerateBill(savedNewVisitJSON.shouldGenerateBill || false);
          setDiscountInPercentage(savedNewVisitJSON.discountInPercentage || 0);
        }
      }
      setDataRestored(true);
    };

    setDataRestored(false);
    setClinicalServices();
    getPhysicians();
    restoreData();
  }, [auth, newAppointmentTempId]);

  useEffect(() => {
    if (!selectedService) return;
    const fees = selectedService.fees;
    if (!discountInPercentage || discountInPercentage === 0) {
      setpayableAmount(fees);
      return;
    }
    const discountedAmount = fees - (fees * discountInPercentage) / 100;

    setpayableAmount(discountedAmount);
  }, [discountInPercentage, selectedService]);

  const onScheduledDateTimeChange = (choosenDate, fromTime, toTime) => {
    if (choosenDate && fromTime && toTime) {
      const startDateTime = dayjs(choosenDate)
        .set("hour", fromTime.hour())
        .set("minute", fromTime.minute());
      const endDateTime = dayjs(choosenDate)
        .set("hour", toTime.hour())
        .set("minute", toTime.minute());
      if (!endDateTime.isAfter(startDateTime)) {
        showSnackbar("End date time should be after start date time.", "error");
        return;
      }
      setScheduledStartDateTime(startDateTime);
      setScheduledEndDateTime(endDateTime);
    }
  };

  const addVisit = async () => {
    setAddingAppointment(true);

    // Convert advanceAmount - if empty or "0", set to 0
    const parsedAdvanceAmount =
      !advanceAmount || advanceAmount === "0"
        ? 0
        : parseInt(advanceAmount) || 0;

    // Payment mode only needed if advance amount > 0
    const finalPaymentMode = parsedAdvanceAmount > 0 ? paymentMode : null;

    const result = await createNewVisit(
      auth,
      selectedPatient.id,
      selectedPhysicianId,
      convertToUTC(scheduledStartDateTime),
      convertToUTC(scheduledEndDateTime),
      patientNotes,
      visitType,
      selectedServiceId,
      onwardTravelTimeInMins,
      returnTravelTimeInMins,
      referredBy,
      parsedAdvanceAmount,
      finalPaymentMode,
      shouldGenerateBill,
      discountInPercentage
    );

    if (result.isSuccess) {
      showSnackbar("Visit created successfully.", "success");
      closeTab(newAppointmentTempId);
    } else {
      showSnackbar("Error occurred while creating the visit.", "error");
    }

    setAddingAppointment(false);
  };
  return (
    <Grid2 container>
      <Grid2 container size={12}>
        {/* Save Visit Action */}
        <Grid2
          container
          size={12}
          sx={{ justifyContent: "start", alignItems: "center" }}
        >
          <Grid2 size={12}>
            {addingAppointment && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(216, 214, 214, 0.4)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 1301,
                }}
              >
                {showLoadingImage()}
              </div>
            )}

            <MenuBar
              showSave={true}
              onSave={() => addVisit()}
              saveLabel="Save Visit"
              saveTooltip="New Visit"
              disabled={
                addingAppointment ||
                !selectedPatient ||
                !selectedPhysicianId ||
                !selectedServiceId ||
                !scheduledStartDateTime ||
                !scheduledEndDateTime
              }
            />
          </Grid2>
        </Grid2>
      </Grid2>
      <Grid2
        size={{ xs: 12, sm: 12, md: 12, lg: 9, xl: 9 }}
        container
        spacing={2}
        sx={{
          ml: "5px",
          maxHeight: "calc(100vh - 150px)",
          overflowY: "auto",
          mt:"5px"
        }}
      >
        <Card className="outerCard" variant="outlined">
          <Grid2 size={12} container spacing={2} sx={{ padding: 2 }}>
            <Grid2 container size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
              <Grid2 size={12} item>
                <SearchSelectPatient1
                  setSelectedPatient={setSelectedPatient}
                  disabled={addingAppointment}
                />
              </Grid2>
              <Grid2 container size={12} item>
                <Grid2 container size={12}>
                  <GenericDropDown
                    label="Primary Service"
                    value={selectedServiceId || ""}
                    onChange={(e) => {
                      setSelectedServiceId(e.target.value);
                      setSelectedService(
                        availableServices.find((s) => s.id === e.target.value)
                      );
                    }}
                    options={
                      availableServices?.map((s) => ({
                        value: s.id,
                        label: `${s.name}, Fees Rs. ${s.fees}, Duration ${s.durationInMins} Mins`,
                      })) || []
                    }
                    required={false}
                    error={false}
                    name="primaryService"
                    disabled={addingAppointment}
                    sx={{
                      "& .MuiPaper-root": {
                        maxHeight: 300,
                        maxWidth: "90vw",
                      },
                      "& .MuiMenuItem-root": {
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      },
                    }}
                  />
                </Grid2>

                <Grid2 container size={12}>
                  <GenericDropDown
                    label="Physician"
                    value={selectedPhysicianId || ""}
                    onChange={(e) => {
                      setSelectedPhysicianId(e.target.value);
                      setSelectedPhysician(
                        availablePhysicians.find((p) => p.id === e.target.value)
                      );
                    }}
                    options={
                      availablePhysicians?.map((p) => ({
                        value: p.id,
                        label: `${p.salutation} ${p.firstName} ${p.lastName}, ${p.suffix}`,
                      })) || []
                    }
                    required={false}
                    error={false}
                    name="physician"
                    disabled={addingAppointment}
                  />
                </Grid2>
              </Grid2>
              {visitType === VisitTypes.Home && (
                <Fragment>
                  <Grid2 size={6}>
                    <GenericDropDown
                      label="Travel Time (Onward)"
                      value={onwardTravelTimeInMins || ""}
                      onChange={(e) => {
                        setOnwardTravelTimeInMins(e.target.value);
                      }}
                      options={[...Array(12)].map((_, index) => {
                        const minutes = (index + 1) * 5;
                        return {
                          value: minutes,
                          label: `${minutes} mins`,
                        };
                      })}
                      required={false}
                      error={false}
                      name="onwardTravelTime"
                      disabled={addingAppointment}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <GenericDropDown
                      label="Travel Time (Return)"
                      value={returnTravelTimeInMins || ""}
                      onChange={(e) => {
                        setReturnTravelTimeInMins(e.target.value);
                      }}
                      options={[...Array(12)].map((_, index) => {
                        const minutes = (index + 1) * 5;
                        return {
                          value: minutes,
                          label: `${minutes} mins`,
                        };
                      })}
                      required={false}
                      error={false}
                      name="returnTravelTime"
                      disabled={addingAppointment}
                    />
                  </Grid2>
                </Fragment>
              )}
              <Grid2 size={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <GenericDatePicker
                    label="Visit Date"
                    value={selectedDate}
                    onChange={(value) => {
                      setSelectedDate(dayjs(value));  
                      onScheduledDateTimeChange(
                        value,
                        scheduledFrom,
                        scheduledTo
                      );
                    }}
                    // minDate={dayjs(new Date())}
                  />
                </LocalizationProvider>
              </Grid2>
              <Grid2 size={6} backgroundColor="white">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    label="From"
                    value={scheduledFrom}
                    disabled={addingAppointment}
                    onChange={(value) => {
                      setScheduledFrom(value);
                      setScheduledTo(
                        value?.add(selectedService.durationInMins, "minute")
                      );
                      onScheduledDateTimeChange(
                        selectedDate,
                        value,
                        value?.add(selectedService.durationInMins, "minute")
                      );
                    }}
                    slotProps={{ textField: { size: "small" } }}
                  ></TimePicker>
                </LocalizationProvider>
              </Grid2>

              <Grid2 size={6} backgroundColor="white">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    label="To"
                    value={scheduledTo}
                    disabled={addingAppointment}
                    minTime={scheduledFrom}
                    onChange={(value) => {
                      setScheduledTo(value);
                      onScheduledDateTimeChange(
                        selectedDate,
                        scheduledFrom,
                        value
                      );
                    }}
                    slotProps={{ textField: { size: "small" } }}
                  ></TimePicker>
                </LocalizationProvider>
              </Grid2>
              <Grid2 container size={12}>
                <GenericTextField
                  label="Referred By"
                  value={referredBy}
                  onChange={(e) => setReferredBy(e.target.value)}
                  disabled={addingAppointment}
                  slotProps={{
                    inputLabel: { shrink: true },
                  }}
                  fullWidth
                  placeholder="Enter referring doctor/clinic name"
                />
                <Grid2 container size={12}>
                  <GenericTextField
                    label="Notes"
                    value={patientNotes}
                    multiline
                    minRows={3}
                    maxRows={3}
                    disabled={addingAppointment}
                    onChange={(e) => {
                      setPatientNotes(e.target.value);
                    }}
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  />
                </Grid2>
                <Grid2 container size={12}>
                  <Grid2 container size={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={shouldGenerateBill}
                          onChange={(e) => {
                            setShouldGenerateBill(e.target.checked);
                          }}
                          disabled={addingAppointment}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Generate bill
                        </Typography>
                      }
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <GenericTextField
                      label="Advanced Amount"
                      value={advanceAmount}
                      onChange={(e) => setAdvanceAmount(e.target.value)}
                      disabled={addingAppointment || !shouldGenerateBill}
                      slotProps={{
                        inputLabel: { shrink: true },
                      }}
                      fullWidth
                      type="number"
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <GenericDropDown
                      label="Payment Mode"
                      margin="dense"
                      value={paymentMode || ""}
                      onChange={(e) => setPaymentMode(e.target.value)}
                      options={PaymentModes}
                      required={!!advanceAmount}
                      error={!!advanceAmount && !paymentMode}
                      name="paymentMode"
                      disabled={
                        addingAppointment ||
                        !shouldGenerateBill ||
                        !advanceAmount
                      }
                    />
                  </Grid2>
                  <Grid2 container size={6} mb={2}>
                    <GenericTextField
                      label="Discount (%)"
                      fullWidth
                      margin="dense"
                      type="number"
                      value={discountInPercentage}
                      onChange={(e) => setDiscountInPercentage(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                      disabled={addingAppointment || !shouldGenerateBill}
                      placeholder="Enter discount percentage"
                      size="small"
                      inputProps={{ min: 1, max: 100 }}
                    />
                  </Grid2>
                  <Grid2 container size={6} mb={2}>
                    <GenericTextField
                      label="Total Billed Amount"
                      fullWidth
                      margin="dense"
                      type="number"
                      value={payableAmount}
                      size="small"
                      inputProps={{ readOnly: true }}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>
            </Grid2>
            <Grid2 container size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
              <SchedulingAssistant
                physician={selectedPhysician}
                selectedDate={selectedDate}
                newSlot={{
                  from: scheduledFrom,
                  to: scheduledTo,
                  new: true,
                  title: "New Visit",
                  onwardTravelTime: onwardTravelTimeInMins,
                  returnTravelTime: returnTravelTimeInMins,
                }}
              />
            </Grid2>
          </Grid2>
        </Card>
      </Grid2>
    </Grid2>
  );
};

export default NewVisit;
